import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { IDSButton, IDSAlert, IDSTooltip, IDSCard } from "@inera/ids-react";
import { setTestExecutionStatus, downloadDecisionDocument } from "../services/testExecutionAPI";
import useReviewReport from "../components/testreports/review/useReviewReport";
import useSubmissionReview from "../components/testreports/review/useSubmissionReview";
import {
  SubmissionHeader,
  SubmissionMeta,
  SubmissionTestResults,
  SubmissionDeviations,
  SubmissionAnswers,
} from "../components/testreports/review/SubmissionReviewShared";
import ConfirmDialog from "../components/ConfirmDialog";

/**
 * Tester's view of their own submitted self-declaration.
 * Read-only answers/results, plus – during clarification – the ability to
 * reply to the reviewer's questions and resubmit for review.
 */
const TesterReview = () => {
  const navigate = useNavigate();
  const { report, setReport, loading } = useReviewReport();

  const review = useSubmissionReview(report);
  const [resubmitError, setResubmitError] = useState(null);
  const [downloadError, setDownloadError] = useState(null);
  const [showResubmitConfirm, setShowResubmitConfirm] = useState(false);

  const handleDownloadDecision = () => {
    setDownloadError(null);
    downloadDecisionDocument(report.id).catch((err) => {
      console.error("Error downloading decision document:", err);
      setDownloadError(err.message || "Kunde inte hämta beslutsunderlaget.");
    });
  };

  if (loading) {
    return (
      <div style={{ maxWidth: 950, margin: "2rem auto", padding: "0 1rem" }}>
        <p>Laddar rapport...</p>
      </div>
    );
  }

  if (!report) {
    return (
      <div style={{ maxWidth: 950, margin: "2rem auto", padding: "0 1rem" }}>
        <IDSAlert type="attention"><p>Ingen rapport vald. Öppna den från Testrapporter.</p></IDSAlert>
        <IDSButton className="ids-mt-4" secondary onClick={() => navigate("/testrapporter")}>Till testrapporter</IDSButton>
      </div>
    );
  }

  const { sections, groupedTests, reviewScenario, badge, commentsByQuestion, addComment, removeComment, flaggedUnresponded } = review;
  const refreshAndLeave = () => navigate("/testrapporter");

  // Keeps report.testResults in sync when a test-result comment is added/edited/removed —
  // otherwise "flaggedUnresponded" (and the resubmit-blocked check) only pick it up after a
  // full page reload.
  const handleTestResultCommentsChanged = (testResultId, comments) => {
    setReport((prev) => ({
      ...prev,
      testResults: (prev.testResults || []).map((tr) =>
        tr.id === testResultId ? { ...tr, comments } : tr
      ),
    }));
  };

  // Tester may comment only while a clarification has been requested.
  const canAddComments = report.status === "IN_CLARIFY";
  // Must respond to every commented question before resubmitting.
  const resubmitBlocked = report.status === "IN_CLARIFY" && flaggedUnresponded.length > 0;

  const handleResubmit = () => {
    setResubmitError(null);
    setShowResubmitConfirm(false);
    setTestExecutionStatus(report.id, "IN_REVIEW")
      .then(refreshAndLeave)
      .catch((e) => {
        console.error(e);
        // 409 is the backend's re-check that every reviewer comment has actually been answered —
        // it can trigger even when the button looked unblocked (e.g. a reply was since removed).
        setResubmitError(
          e.status === 409
            ? "Alla kommentarer från granskaren måste besvaras innan du kan skicka in igen."
            : "Kunde inte skicka in för granskning. Försök igen."
        );
      });
  };

  const isFinished = report.status === "APPROVED" || report.status === "REJECTED";

  return (
    <div style={{ maxWidth: 1200, margin: "2rem auto", padding: "0 1rem", display: "flex", gap: "1.5rem", alignItems: "flex-start" }}>
      <div style={{ flex: "1 1 auto", minWidth: 0 }}>
      <SubmissionHeader report={report} badge={badge} breadcrumbLabel="Testrapporter" title="Självdeklaration" />
      <SubmissionMeta report={report} reviewScenario={reviewScenario} />

      <SubmissionTestResults report={report} groupedTests={groupedTests} userRole="TESTER" canAddComments={canAddComments}
        onTestResultCommentsChanged={handleTestResultCommentsChanged} />

      <SubmissionDeviations deviations={report.deviations || []} testResults={report.testResults || []} />

      <SubmissionAnswers
        report={report}
        sections={sections}
        commentsByQuestion={commentsByQuestion}
        isReviewer={false}
        showUnexpectedFilter={false}
        unexpectedCount={0}
        canAddComments={canAddComments}
        canEditAnswers={canAddComments}
        reviewScenario={reviewScenario}
        onAdded={addComment}
        onDeleted={removeComment}
      />

      {/* Final decision, once the reviewer has decided */}
      {isFinished && (
        <IDSCard className="ids-mt-6">
          <h2 className="ids-heading-m">Beslut</h2>
          <p><strong>Beslut:</strong> {report.status === "APPROVED" ? "Godkänd" : "Underkänd"}</p>
          {report.decisionComment && (
            <p style={{ whiteSpace: "pre-wrap" }}>
              <strong>Kommentar:</strong> {report.decisionComment}
            </p>
          )}
          {report.hasDecisionDocument && (
            <div className="ids-mt-2">
              <IDSButton size="s" secondary onClick={handleDownloadDecision}>
                Hämta beslutsunderlag (PDF)
              </IDSButton>
            </div>
          )}
          {downloadError && (
            <div className="ids-mt-2">
              <IDSAlert type="error"><p>{downloadError}</p></IDSAlert>
            </div>
          )}
        </IDSCard>
      )}

      {/* Resubmit for review after a clarification request */}
      {report.status === "IN_CLARIFY" && (
        <section style={{ marginTop: "2rem" }}>
          <div className="ids-flex" style={{ gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
            <IDSButton size="m" onClick={() => { setResubmitError(null); setShowResubmitConfirm(true); }} disabled={resubmitBlocked}>Skicka in för granskning</IDSButton>
            {resubmitBlocked && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", color: "var(--ids-palette-error-40)" }}>
                Svara på granskarens kommentarer först.
                <IDSTooltip
                  position="top"
                  maxWidth=""
                  trigger={
                    <span
                      aria-label="Visa vilka frågor som behöver ett svar"
                      className="ids-icon-information ids-icon--m ids-icon--color-preset-1 ids-icon--interactive"
                      role="button"
                      style={{ position: "relative", zIndex: 4 }}
                      tabIndex={0}
                    />
                  }
                >
                  <strong>Kommentera dessa frågor:</strong>
                  <ul style={{ margin: "0.3rem 0 0", paddingLeft: "1.1rem" }}>
                    {flaggedUnresponded.map((f, i) => (
                      <li key={i}>{f.section}: {f.question}</li>
                    ))}
                  </ul>
                </IDSTooltip>
              </span>
            )}
          </div>
          {resubmitError && (
            <div className="ids-mt-3">
              <IDSAlert type="error"><p>{resubmitError}</p></IDSAlert>
            </div>
          )}
          <ConfirmDialog
            isOpen={showResubmitConfirm}
            title="Skicka in underlaget för granskning?"
            confirmLabel="Ja, skicka in"
            cancelLabel="Avbryt"
            onCancel={() => setShowResubmitConfirm(false)}
            onConfirm={handleResubmit}
          >
            <div style={{ marginBottom: "1.5rem", color: "#333" }}>
              <p style={{ marginTop: 0 }}>
                Endast <strong>en deklaration per organisation</strong> tas emot. Det underlag du
                skickar in blir organisationens deklaration för det här tjänstekontraktet.
              </p>
              <p style={{ marginBottom: 0 }}>
                När underlaget är inskickat kan du inte ändra dina svar eller kommentarer förrän
                granskaren begär en ny komplettering.
              </p>
            </div>
          </ConfirmDialog>
        </section>
      )}
      </div>
    </div>
  );
};

export default TesterReview;
